import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';

import { ErrorResponse } from '../../../custom-fetch';
import { Route } from '../../../routes';
import { QueryKey } from '../../../services/query-keys';
import { useHeaderLinksService } from '../../../services/header-links-service';
import { IHeaderLink } from '../../../shared/IHeaderLink';
import ErrorsAlert from '../../ErrorsAlert/ErrorsAlert';
import FormButtons from '../../ui/FormButtons/FormButtons';
import { IInputField } from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import Table from '../../ui/Table/Table';
import HeaderSublinkEdit from './HeaderSublinkEdit';
import { RefHandle } from '../../../shared/RefHandle';
import { useInputs } from '../../ui/Input/useInputs';
import { EHeaderLinkInputs, getHeaderLinkInputs, formatHeaderLinkForInput, formatHeaderLinkForSubmit } from './form-utils';

interface IProps {
  id: string;
  link?: IHeaderLink;
}

const HeaderLinkEdit: React.FC<IProps> = ({ id, link }) => {
  const isEdit = id !== "add";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { saveHeaderLink, deleteHeaderLink } = useHeaderLinksService();

  const [sublinks, setSublinks] = useState<IHeaderLink[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (link && !isInitialized) {
      setSublinks(link.sublinks || []);
      setIsInitialized(true);
    }
  }, [link, isInitialized]);

  const { mutate, isPending, isError, error } = useMutation<
    IHeaderLink,
    ErrorResponse,
    IHeaderLink
  >({
    mutationFn: saveHeaderLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.headerLinks] });
      navigate(Route.headerLinks);
    },
  });

  const {
    mutate: deleteMutate,
    isPending: isDeleting,
    isError: isDeletingError,
    error: deletingError,
  } = useMutation<boolean, ErrorResponse>({
    mutationFn: () => deleteHeaderLink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.headerLinks] });
      navigate(Route.headerLinks);
    },
  });

  const [inputs, setInputs] = useState<IInputField>(getHeaderLinkInputs(true));

  const formattedData = useMemo(() => formatHeaderLinkForInput(link), [link]);

  const { createInput, submit } = useInputs({
    data: formattedData,
    inputs,
    setInputs,
  });

  const sublinksRef = useRef<RefHandle<IHeaderLink>[]>([]);

  const submitHandler = async () => {
    const data = await submit();
    if (data) {
      const sublinkData: IHeaderLink[] = [];
      const currentRefs = sublinksRef.current;
      for (let i = 0; i < currentRefs.length; i++) {
        const ref = currentRefs[i];
        if (ref) {
          const sub = await ref.getData();
          if (sub) {
            // Strip temp IDs
            const { id: subId, ...rest } = sub;
            sublinkData.push({
              ...rest,
              id: (subId && subId.startsWith('temp-')) ? undefined : subId
            } as IHeaderLink);
          }
        }
      }

      mutate({ 
        ...formatHeaderLinkForSubmit(data), 
        id: isEdit ? id : undefined,
        ordering: link?.ordering ?? 0,
        sublinks: sublinkData 
      });
    }
  };

  const deleteHandler = () => {
    if (window.confirm("Are you sure you want to delete this link?")) {
      deleteMutate();
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(sublinks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSublinks(items.map((item, index) => ({ ...item, ordering: index })));
  };

  const addSublink = () => {
    const newSublink: IHeaderLink = {
      id: `temp-${Date.now()}`,
      text: "New Sublink",
      link: "",
      ordering: sublinks.length,
      isVisible: true,
    };
    setSublinks([...sublinks, newSublink]);
  };

  const removeSublink = (index: number) => {
    const items = [...sublinks];
    items.splice(index, 1);
    setSublinks(items.map((item, index) => ({ ...item, ordering: index })));
  };

  return (
    <div style={{ padding: '20px' }}>
      {createInput(EHeaderLinkInputs.text)}
      {createInput(EHeaderLinkInputs.link)}
      {createInput(EHeaderLinkInputs.isVisible)}

      <div style={{ marginTop: '20px' }}>
        <h3>Sublinks</h3>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="sublinks">
            {(provided) => (
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Text</th>
                    <th>Link</th>
                    <th>Visible</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {sublinks.map((sub, index) => (
                    <HeaderSublinkEdit 
                      key={sub.id || index}
                      sublink={sub}
                      index={index}
                      onRemove={removeSublink}
                      ref={(handle) => { sublinksRef.current[index] = handle!; }}
                    />
                  ))}
                  {provided.placeholder}
                </tbody>
              </Table>
            )}
          </Droppable>
        </DragDropContext>
        <Button onClick={addSublink} style={{ marginTop: '10px' }}>Add Sublink</Button>
      </div>

      {isError && error && <ErrorsAlert errors={error.messages} />}
      {isDeletingError && deletingError && <ErrorsAlert errors={deletingError.messages} />} 
      
      <div style={{ marginTop: '30px' }}>
        <FormButtons
          onSubmit={submitHandler}
          isLoading={isPending}
          onDelete={isEdit ? deleteHandler : undefined}
          isDeleting={isDeleting}
          deleteConfirmMessage="Are you sure you want to delete this header link?"
        />
      </div>
    </div>
  );
};

export default HeaderLinkEdit;
